
import { Link, useLocation } from "react-router-dom";
import { memo, useState } from "react";
import { cn } from "@/lib/utils";
import { Menu, X, User, LogOut } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = memo(() => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img 
                src="/lovable-uploads/09f539e7-7152-48ef-9cdc-06aeebe1948b.png"
                alt="AIMAA Logo"
                width="180"
                height="48"
                className="h-12 w-auto"
              />
            </Link>
          </div>
          
          {isMobile ? (
            <>
              <div className="flex items-center space-x-2">
                {user && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full">
                        <User className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>
                        <div className="flex flex-col">
                          <span className="font-medium">{user.fullName}</span>
                          <span className="text-xs text-gray-500">{user.email}</span>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={logout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Logout</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                
                <button 
                  onClick={toggleMenu}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-[#0f2d69] focus:outline-none"
                  aria-expanded={isMenuOpen}
                >
                  <span className="sr-only">{isMenuOpen ? 'Close menu' : 'Open menu'}</span>
                  {isMenuOpen ? (
                    <X className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Menu className="block h-6 w-6" aria-hidden="true" />
                  )}
                </button>
              </div>
              
              {isMenuOpen && (
                <div className="absolute top-20 left-0 w-full bg-white shadow-lg z-50">
                  <div className="px-2 pt-2 pb-3 space-y-1">
                    <Link
                      to="/about"
                      className={cn(
                        "block px-3 py-2 rounded-md text-base font-medium",
                        isActive("/about") 
                          ? "text-[#0f2d69] bg-[#0f2d69]/5"
                          : "text-gray-900 hover:text-[#0f2d69] hover:bg-[#0f2d69]/5"
                      )}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      About
                    </Link>
                    <Link
                      to="/methodology"
                      className={cn(
                        "block px-3 py-2 rounded-md text-base font-medium",
                        isActive("/methodology")
                          ? "text-[#0f2d69] bg-[#0f2d69]/5"
                          : "text-gray-900 hover:text-[#0f2d69] hover:bg-[#0f2d69]/5"
                      )}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Methodology
                    </Link>
                    <Link
                      to="/assessment"
                      className={cn(
                        "block px-3 py-2 rounded-md text-base font-medium",
                        isActive("/assessment")
                          ? "text-[#0f2d69] bg-[#0f2d69]/5"
                          : "text-gray-900 hover:text-[#0f2d69] hover:bg-[#0f2d69]/5"
                      )}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Assessment
                    </Link>
                    <Link
                      to="/assessment-history"
                      className={cn(
                        "block px-3 py-2 rounded-md text-base font-medium",
                        isActive("/assessment-history")
                          ? "text-[#0f2d69] bg-[#0f2d69]/5"
                          : "text-gray-900 hover:text-[#0f2d69] hover:bg-[#0f2d69]/5"
                      )}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Assessment History
                    </Link>
                    {!user && (
                      <Link
                        to="/login"
                        className="block px-3 py-2 rounded-md text-base font-medium text-[#0f2d69] hover:bg-[#0f2d69]/5"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Login
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center space-x-12">
              <Link
                to="/about"
                className={cn(
                  "inline-flex items-center text-base font-medium transition-colors",
                  isActive("/about") 
                    ? "text-[#0f2d69]"
                    : "text-gray-900 hover:text-[#0f2d69]"
                )}
              >
                About
              </Link>
              <Link
                to="/methodology"
                className={cn(
                  "inline-flex items-center text-base font-medium transition-colors",
                  isActive("/methodology")
                    ? "text-[#0f2d69]"
                    : "text-gray-900 hover:text-[#0f2d69]"
                )}
              >
                Methodology
              </Link>
              <Link
                to="/assessment"
                className={cn(
                  "inline-flex items-center text-base font-medium transition-colors",
                  isActive("/assessment")
                    ? "text-[#0f2d69]"
                    : "text-gray-900 hover:text-[#0f2d69]"
                )}
              >
                Assessment
              </Link>
              <Link
                to="/assessment-history"
                className={cn(
                  "inline-flex items-center text-base font-medium transition-colors",
                  isActive("/assessment-history")
                    ? "text-[#0f2d69]"
                    : "text-gray-900 hover:text-[#0f2d69]"
                )}
              >
                Assessment History
              </Link>
              
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2">
                      <User className="h-4 w-4" />
                      {user.fullName}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.fullName}</span>
                        <span className="text-xs text-gray-500">{user.email}</span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Logout</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="gap-2">
                    <User className="h-4 w-4" />
                    Login
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
});

Navbar.displayName = "Navbar";

export default Navbar;
